import { useMemo, useEffect, useRef } from 'react';
import { FilterOption } from '@components/common/FilterButtons';

interface UseScheduleConflictFilterOptions {
  filterCounts: Record<string, number>;
  isLoading: boolean;
  currentFilter: string;
  /** Initial/default filter for the page (used to detect if we're on default state for auto-activation) */
  initialFilter: string;
  /** Target filter to switch to when all conflicts are resolved */
  autoSwitchTargetFilter: string;
  handleFilterChange: (filter: string) => void;
  debouncedSearchQuery?: string; // Optional, not used anymore but kept for backward compatibility
  hasActiveColumnFilters?: boolean; // Optional, not used anymore but kept for backward compatibility
  baseFilters: FilterOption[];
  /** Additional custom logic to determine if other filters are active (e.g., checkbox filters) */
  hasOtherActiveFilters?: boolean; // Optional, not used anymore but kept for backward compatibility
  /** Whether to auto-activate conflicts filter on page load when conflicts exist (default: true) */
  autoActivateConflicts?: boolean;
  /**
   * Keep the Conflicts tab selected (and visible) while it is the active filter even when there are no
   * conflicts, instead of switching to autoSwitchTargetFilter. For a dashboard drill-down whose tile
   * counted the conflicts and expects the list to show exactly that set, empty included.
   */
  keepConflictsTab?: boolean;
}

interface UseScheduleConflictFilterReturn {
  stateFilters: FilterOption[];
}

/**
 * Hook for managing conflict filter button behavior in schedule pages
 *
 * Features:
 * - Dynamic conflicts button visibility (red "danger" type) based on conflictsTotal from backend
 * - Auto-activation when conflicts detected on page load (only once)
 * - Auto-redirect when all conflicts resolved (switches to target filter)
 * - Persistence when conflicts hidden by search/filters (button remains visible using conflictsTotal)
 *
 * Backend provides:
 * - filterCounts.conflicts - current count with search/columnFilters applied
 * - filterCounts.conflictsTotal - total count without search/columnFilters (for tab visibility)
 *
 * @param initialFilter - The default filter for the page (e.g., 'pending_data_enrichment', 'scheduled')
 * @param autoSwitchTargetFilter - Filter to switch to when all conflicts are resolved
 */
export function useScheduleConflictFilter({
  filterCounts,
  isLoading,
  currentFilter,
  initialFilter,
  autoSwitchTargetFilter,
  handleFilterChange,
  debouncedSearchQuery,
  hasActiveColumnFilters,
  baseFilters,
  hasOtherActiveFilters = false,
  autoActivateConflicts = true,
  keepConflictsTab = false
}: UseScheduleConflictFilterOptions): UseScheduleConflictFilterReturn {
  // Track if initial load happened to auto-activate conflicts filter only once
  const hasAutoActivatedConflicts = useRef(false);

  // State filter options - show conflicts button based on conflictsTotal from backend
  const stateFilters = useMemo(() => {
    const filters = [...baseFilters];

    // Show conflicts button if conflictsTotal > 0 (backend provides total without search/columnFilters)
    // Use conflictsTotal if available (new API), otherwise fall back to conflicts count
    const conflictsTotal = filterCounts.conflictsTotal ?? filterCounts.conflicts ?? 0;
    const shouldShowConflicts = conflictsTotal > 0 || (keepConflictsTab && currentFilter === 'conflicts');

    if (shouldShowConflicts) {
      filters.unshift({
        key: 'conflicts',
        label: 'Conflicts',
        type: 'danger' as const,
        tooltip: 'Meetings with scheduling conflicts — these are also counted in their status tab'
      });
    }

    return filters;
  }, [filterCounts, baseFilters, keepConflictsTab, currentFilter]);

  // Auto-activate conflicts filter when page loads with conflicts (only once)
  useEffect(() => {
    if (
      autoActivateConflicts &&
      !isLoading &&
      !hasAutoActivatedConflicts.current &&
      filterCounts.conflicts > 0 &&
      currentFilter === initialFilter
    ) {
      hasAutoActivatedConflicts.current = true;
      handleFilterChange('conflicts');
    }
  }, [autoActivateConflicts, filterCounts.conflicts, isLoading, currentFilter, initialFilter, handleFilterChange]);

  // Auto-switch to target filter when all conflicts are resolved
  useEffect(() => {
    const conflictsTotal = filterCounts.conflictsTotal ?? filterCounts.conflicts ?? 0;
    if (
      !keepConflictsTab &&
      !isLoading &&
      currentFilter === 'conflicts' &&
      filterCounts.conflicts === 0 &&
      conflictsTotal === 0
    ) {
      handleFilterChange(autoSwitchTargetFilter);
    }
  }, [filterCounts, isLoading, currentFilter, handleFilterChange, autoSwitchTargetFilter, keepConflictsTab]);

  return {
    stateFilters
  };
}
